// SPINLAB — services, pricing, and vehicle categories.
// Single source of truth: Services page and Pricing page both read from here,
// so they always show identical information.

export const VEHICLE_CATEGORIES = [
  "Sedan / Coupe",
  "Compact & Mid-Size SUV / Crossover (2-Row)",
  "Full-Size SUV / 3-Row SUV / Truck / Minivan",
];

// Core detailing packages
export const services = [
  {
    slug: "exterior-detail",
    name: "Exterior Detail",
    duration: "Approx. 1.5 Hours",
    summary:
      "A thorough, scratch-safe hand wash designed to properly clean the vehicle's exterior and restore a clean, glossy finish.",
    includes: [
      "Scratch-safe hand wash",
      "Paint decontamination as needed",
      "Deep cleaning of wheels, tires and wheel wells",
      "Door and trunk jambs cleaned",
      "Tire and trim dressing",
      "Gloss-enhancing spray sealant",
    ],
    pricing: [
      { vehicle: "Sedan / Coupe", price: 100 },
      { vehicle: "Compact & Mid-Size SUV / Crossover (2-Row)", price: 125 },
      { vehicle: "Full-Size SUV / 3-Row SUV / Truck / Minivan", price: 150 },
    ],
  },
  {
    slug: "interior-detail",
    name: "Interior Detail",
    duration: "Approx. 3–5 Hours",
    summary:
      "A complete deep cleaning of the vehicle's interior, including seats, carpets, floor mats and all reachable interior surfaces.",
    includes: [
      "Complete interior detailing of all reachable surfaces",
      "Seats, carpets and floor mats deep cleaned",
      "Spot and stain treatment as needed",
      "Dashboard, center console and storage compartments detailed",
      "Crevices, vents and hard-to-reach areas detailed",
      "Plastic and leather conditioned where applicable",
      "Trunk or cargo area cleaned",
      "Headliner spot cleaned as needed",
      "Crystal-clear interior windows",
    ],
    pricing: [
      { vehicle: "Sedan / Coupe", price: 180 },
      { vehicle: "Compact & Mid-Size SUV / Crossover (2-Row)", price: 220 },
      { vehicle: "Full-Size SUV / 3-Row SUV / Truck / Minivan", price: 250 },
    ],
  },
  {
    slug: "full-detail",
    name: "Full Detail",
    duration: "Approx. 4–6 Hours",
    badge: "Best Value",
    summary: "Complete interior and exterior detailing in one appointment.",
    includes: [
      "Everything included in the Exterior Detail",
      "Everything included in the Interior Detail",
    ],
    pricing: [
      { vehicle: "Sedan / Coupe", price: 250 },
      { vehicle: "Compact & Mid-Size SUV / Crossover (2-Row)", price: 280 },
      { vehicle: "Full-Size SUV / 3-Row SUV / Truck / Minivan", price: 320 },
    ],
  },
];

// Paint correction & coatings
export const paintServices = [
  {
    slug: "one-step-paint-correction",
    name: "One-Step Paint Correction",
    duration: "Approx. 3–5 Hours",
    summary:
      "A one-stage machine polishing service designed to remove or significantly reduce light swirl marks, oxidation, haze and minor paint imperfections while restoring gloss and clarity. Deep scratches and severe paint defects may remain.",
    includes: [
      "Exterior wash",
      "Paint decontamination",
      "Paint preparation",
      "One-stage machine polishing",
      "Final paint finish",
    ],
    pricing: [
      { vehicle: "Sedan / Coupe", price: 290 },
      { vehicle: "Compact & Mid-Size SUV / Crossover (2-Row)", price: 340 },
      { vehicle: "Full-Size SUV / 3-Row SUV / Truck / Minivan", price: 390 },
    ],
    footnote:
      "Final pricing and service time depend on vehicle size, paint condition and the level of correction required.",
  },
  {
    slug: "multi-step-paint-correction",
    name: "Multi-Step Paint Correction",
    duration: "Custom timeline",
    custom: true,
    summary:
      "A more intensive paint correction service for vehicles with heavier swirl marks, oxidation, scratches and other visible paint defects. The required correction process, expected results, service time and final price are determined after inspecting the vehicle and discussing the customer's expectations.",
    includes: [
      "In-person or photo-based paint inspection",
      "Wash, decontamination and paint preparation",
      "Multi-stage machine polishing",
      "Final paint finish tailored to the vehicle's condition",
    ],
    pricing: [],
    priceLabel: "Custom Quote — Available Upon Request",
  },
  {
    slug: "ceramic-coating",
    name: "Ceramic Coating",
    duration: "Custom timeline",
    summary:
      "A ceramic coating service that includes proper exterior cleaning, paint preparation and the appropriate level of paint correction before the coating is applied.",
    includes: [
      "Full exterior decontamination and preparation",
      "Level of paint correction matched to the paint condition",
      "Ceramic coating application",
      "Curing and final inspection",
    ],
    pricing: [
      { vehicle: "Sedan / Coupe", price: 600 },
      { vehicle: "Compact & Mid-Size SUV / Crossover (2-Row)", price: 700 },
      { vehicle: "Full-Size SUV / 3-Row SUV / Truck / Minivan", price: 800 },
    ],
    footnote:
      "Final pricing depends on vehicle size, paint condition, required level of paint correction, selected coating durability, number of years of protection and expected final results. The final service time and price are confirmed after the vehicle and desired coating option have been evaluated.",
  },
];

// Specialty services
export const specialtyServices = [
  {
    slug: "glass-coating",
    name: "Glass Coating",
    summary:
      "A hydrophobic coating that improves visibility in wet conditions and makes exterior glass easier to clean.",
    pricing: [
      { vehicle: "Windshield", price: 100 },
      { vehicle: "All exterior glass", price: 180 },
    ],
  },
  {
    slug: "headlight-polishing",
    name: "Headlight Polishing",
    summary:
      "Removes or reduces oxidation, restores clarity and improves nighttime visibility.",
    priceFlat: 100,
    priceLabel: "Starting at $100 per pair",
  },
  {
    slug: "paint-chip-touch-ups",
    name: "Paint Chip Touch-Ups",
    summary:
      "Color-matched touch-up paint for small chips and scratches. Paint availability and final pricing depend on the vehicle's paint code. The goal is to make the damage less noticeable and help protect the exposed area. Perfect or invisible results are not guaranteed without professional repainting.",
    priceFlat: 50,
    priceLabel: "Starting at $50",
    addOnNote: "Offered as an add-on service or as part of another qualifying appointment.",
  },
];

// Add-ons (never booked as a stand-alone mobile appointment)
export const addOnServices = [
  { name: "Hand-Applied Wax", priceLabel: "Starting at $40" },
  { name: "Engine Bay Cleaning", priceLabel: "Starting at $60" },
];

export const PRICING_DISCLAIMER =
  "Final pricing depends on vehicle size, condition, level of soiling, stain severity, pet hair, paint condition, selected services, expected results and travel distance. Final pricing is always confirmed before the appointment.";

// About / approach chips (still used elsewhere)
export const approach = [
  "Clear pricing before I start",
  "No hidden fees",
  "No rushed work",
  "No upsell pressure",
  "Attention to detail",
  "Quality over speed",
];
